import { requireFirebaseAuth, firebaseHttpFunctionUrl } from './firebase';

export type ProcessingSource = 'master' | 'mail' | 'responses';

export type OutputReportKind =
  | 'trend'
  | 'case-inflow'
  | 'pec-social-subreason'
  | 'social-reason-entry'
  | 'territorial-analysis';

export type OutputReportAvailability = {
  kind: OutputReportKind;
  year: number;
  months: number[];
};

export type ProcessingColumn = {
  key: string;
  label: string;
  sourceIndex: number;
};

export type ProcessingAvailability = {
  masterYears: number[];
  outputReports?: OutputReportAvailability[];
  mail: Array<{
    year: number;
    months: number[];
    latestReferenceDate: string;
    latestFileName: string;
  }>;
  responses: Array<{
    year: number;
    months: number[];
  }>;
};

const OUTPUT_HISTORY_START_YEAR = 2025;
const OUTPUT_HISTORY_START_MONTH = 12;

function completedOutputMonths(year: number, now: Date): number[] {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const historyStart = OUTPUT_HISTORY_START_YEAR * 12 + OUTPUT_HISTORY_START_MONTH - 1;
  const lastCompletedMonth = currentYear * 12 + currentMonth - 2;

  return Array.from({ length: 12 }, (_, index) => index + 1).filter((month) => {
    const period = year * 12 + month - 1;
    return period >= historyStart && period <= lastCompletedMonth;
  });
}

export function outputReportYears(
  availability: ProcessingAvailability | null | undefined,
  kind: OutputReportKind,
  now = new Date(),
): number[] {
  const generatedYears = (availability?.outputReports ?? [])
    .filter((entry) => entry.kind === kind)
    .map(({ year }) => year);
  const sourceYears = (availability?.masterYears ?? []).filter((year) => (
    kind === 'trend' || completedOutputMonths(year, now).length > 0
  ));
  return [...new Set([...generatedYears, ...sourceYears])]
    .sort((left, right) => right - left);
}

export function outputReportMonths(
  availability: ProcessingAvailability | null | undefined,
  kind: OutputReportKind,
  year: number,
  now = new Date(),
): number[] {
  const entry = availability?.outputReports?.find((item) => item.kind === kind && item.year === year);
  if (Array.isArray(availability?.outputReports)) {
    return [...new Set([
      ...(entry?.months ?? []),
      ...completedOutputMonths(year, now),
    ])].sort((left, right) => left - right);
  }
  return Array.from({ length: 12 }, (_, index) => index + 1);
}

export function hasGeneratedOutputReport(
  availability: ProcessingAvailability | null | undefined,
  kind: OutputReportKind,
  year: number,
  month: number | null,
): boolean {
  if (!Array.isArray(availability?.outputReports)) return true;
  const entry = availability.outputReports.find((item) => item.kind === kind && item.year === year);
  if (!entry) return false;
  return month === null || entry.months.includes(month);
}

export type ProcessingDataset = {
  source: ProcessingSource;
  year: number;
  month: number | null;
  file: {
    id: string;
    name: string;
    path: string;
    modifiedTime: string | null;
    webViewLink: string | null;
  };
  referenceDate: string | null;
  worksheetName: string;
  columns: ProcessingColumn[];
  rowCount: number;
  rows: string[][];
};

export type ProcessingFilters = Record<string, string>;
export type ProcessingSort = { key: string; direction: 'asc' | 'desc' } | null;

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it-IT');
}

function sortableDate(value: string): number | null {
  const italian = value.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:\D+(\d{1,2})[.:](\d{1,2}))?/);
  if (italian) {
    return Date.UTC(
      Number(italian[3]),
      Number(italian[2]) - 1,
      Number(italian[1]),
      Number(italian[4] ?? 0),
      Number(italian[5] ?? 0),
    );
  }
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return iso ? Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : null;
}

function sortableNumber(value: string): number | null {
  const compact = value.trim();
  if (!/^-?\d+(?:[.,]\d+)?$/.test(compact)) return null;
  const parsed = Number(compact.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

const collator = new Intl.Collator('it-IT', { numeric: true, sensitivity: 'base' });

export function compareProcessingValues(
  left: string,
  right: string,
  direction: 'asc' | 'desc',
): number {
  const leftBlank = left.trim() === '';
  const rightBlank = right.trim() === '';
  if (leftBlank !== rightBlank) return leftBlank ? 1 : -1;
  if (leftBlank && rightBlank) return 0;

  const leftDate = sortableDate(left);
  const rightDate = sortableDate(right);
  let compared: number;
  if (leftDate !== null && rightDate !== null) {
    compared = leftDate - rightDate;
  } else {
    const leftNumber = sortableNumber(left);
    const rightNumber = sortableNumber(right);
    compared = leftNumber !== null && rightNumber !== null
      ? leftNumber - rightNumber
      : collator.compare(left, right);
  }
  return direction === 'asc' ? compared : -compared;
}

export function applyProcessingView(
  rows: string[][],
  columns: ProcessingColumn[],
  filters: ProcessingFilters,
  sort: ProcessingSort,
): string[][] {
  const activeFilters = columns
    .map((column, index) => ({ index, value: normalizeText(filters[column.key]?.trim() ?? '') }))
    .filter(({ value }) => value !== '');
  const filtered = activeFilters.length === 0
    ? rows
    : rows.filter((row) => activeFilters.every(({ index, value }) => normalizeText(row[index] ?? '').includes(value)));

  if (!sort) return filtered;
  const columnIndex = columns.findIndex(({ key }) => key === sort.key);
  if (columnIndex < 0) return filtered;
  return [...filtered].sort((left, right) => compareProcessingValues(
    left[columnIndex] ?? '',
    right[columnIndex] ?? '',
    sort.direction,
  ));
}

type StreamMetaLine = {
  type: 'meta';
  dataset: Omit<ProcessingDataset, 'rows'>;
};

export async function fetchProcessingDataset(input: {
  source: ProcessingSource;
  year: number;
  month: number | null;
  signal?: AbortSignal;
  onProgress?: (loadedRows: number, totalRows: number) => void;
}): Promise<ProcessingDataset> {
  const firebaseUser = requireFirebaseAuth().currentUser;
  if (!firebaseUser) throw new Error('Sessione Firebase non disponibile.');
  const requestDataset = async (idToken: string) => fetch(firebaseHttpFunctionUrl('streamProcessingDataset'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ source: input.source, year: input.year, month: input.month }),
    signal: input.signal,
  });

  let response = await requestDataset(await firebaseUser.getIdToken());
  if ((response.status === 401 || response.status === 403) && !input.signal?.aborted) {
    try {
      response = await requestDataset(await firebaseUser.getIdToken(true));
    } catch {
      throw new Error('La sessione è scaduta. Accedi nuovamente per caricare la tabella.');
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(error?.message || 'Non è stato possibile caricare il file Excel.');
  }
  if (!response.body) throw new Error('Il browser non supporta il caricamento progressivo dei dati.');

  const rows: string[][] = [];
  const streamState: { metadata?: Omit<ProcessingDataset, 'rows'> } = {};
  let pending = '';
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  const consumeLine = (line: string) => {
    if (!line) return;
    const parsed = JSON.parse(line) as StreamMetaLine | { type: 'end'; rowCount: number } | string[];
    if (Array.isArray(parsed)) {
      rows.push(parsed);
      if (rows.length % 250 === 0 && streamState.metadata) {
        input.onProgress?.(rows.length, streamState.metadata.rowCount);
      }
    } else if (parsed.type === 'meta') {
      streamState.metadata = parsed.dataset;
      input.onProgress?.(0, parsed.dataset.rowCount);
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    pending += decoder.decode(value, { stream: !done });
    const lines = pending.split('\n');
    pending = lines.pop() ?? '';
    lines.forEach(consumeLine);
    if (done) break;
  }
  consumeLine(pending);
  const metadata = streamState.metadata;
  if (!metadata) throw new Error('La risposta dati non contiene i metadati del file Excel.');
  input.onProgress?.(rows.length, metadata.rowCount);
  return { ...metadata, rows };
}
